import {
    FlowBlock, FlowPort, FlowProperty,
    FlowValidationError, FlowValidationErrorType
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {PropertyType, PropertyWidgetName} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';

export class FlowValidationController
{
    public validate(flowId:number):FlowValidationError[]
    {
        const portErrors = this.validateAllFlowPorts(flowId);
        const propErrors = this.validateAllFlowProperties(flowId);

        return [...portErrors, ...propErrors];
    }

    public validateAllFlowProperties(flowId:number):FlowValidationError[]
    {
        const flow = controllers.ui.flow.getFlowById(flowId);

        if( flow )
        {
            const errors = flow.flowBlocks
                .filter(({isEntryBlock}) => !isEntryBlock)
                .map(block => (
                    this.validateFlowProperties(block.properties, block)
                ));

            return [].concat(...errors);
        }

        return [];
    }

    public validateAllFlowPorts(flowId:number):FlowValidationError[]
    {
        const flow = controllers.ui.flow.getFlowById(flowId);

        if( flow )
        {
            const errors = flow.flowBlocks
                .filter(({isEntryBlock}) => !isEntryBlock)
                .map((flowBlock:FlowBlock) =>
                {
                    const {inputPorts} = flowBlock;

                    return inputPorts
                        .filter((inputPort:FlowPort) => (
                            inputPort.isRequired() &&
                            !flowBlock.inWires.find(({target}) => target.port.id === inputPort.id)
                        ))
                        .map((invalidPort) => (
                            FlowValidationError.portError(
                                [ValidationMessages.requiredPort],
                                flowBlock.id,
                                flowBlock.name,
                                invalidPort.id
                            )
                        ));
                });

            return [].concat(...errors);
        }

        return [];
    }

    public validateFlowProperties(props:FlowProperty[], block:FlowBlock):FlowValidationError[]
    {
        return props
            .map((prop:FlowProperty) =>
            {
                const {definition, value} = prop;
                const {name: propName, isRequired} = definition;
                const messages = [];

                if( value == null || value === '' )
                {
                    if( isRequired )
                    {
                        messages.push(ValidationMessages.nonEmpty);
                    }
                }
                else
                {
                    messages.push(...this._getTypeErrorMessages(prop));
                    messages.push(...this._getWidgetErrorMessages(prop));
                    messages.push(...this._getCustomErrorMessages(prop, props));
                }

                const nestedErrors = this._getNestedErrors(prop, block);

                return FlowValidationError.propertyError(messages, block.id, block.name, propName, nestedErrors);
            })
            .filter((error:FlowValidationError) => error.messages.length > 0 || error.nested.length > 0);
    }

    public buildErrorMessage(error:FlowValidationError)
    {
        const {
            type,
            messages,
            flowBlockId,
            flowBlockName,
            flowPortId,
            flowPropertyName
        } = error;

        const message = messages[0];

        if( type === FlowValidationErrorType.Port )
        {
            return `${message} Component Name: ${flowBlockName}, Port Id: ${flowPortId}`;
        }

        if( type === FlowValidationErrorType.Property )
        {
            return `${message} Component Name: ${flowBlockName}, Property Name: ${flowPropertyName}`;
        }
    }

    public isMapWidgetNewKeyUnique(key:string, mapProp:FlowProperty):boolean
    {
        const {meta} = mapProp;
        const items = meta && meta.items;

        if( !items ) return true;

        const itemsKeyMap = new Map(items.map(({key, value}) => [key, value]));

        return !itemsKeyMap.has(key);
    }

    public isBlockValid(flowId:number, block:FlowBlock):boolean
    {
        const invalidBlockIds = this._getInvalidBlockIds(flowId);

        return invalidBlockIds.indexOf(block.id) === -1;
    }

    public isPortValid(flowId:number, port:FlowPort):boolean
    {
        const invalidPortIds = this._getInvalidPortIds(flowId);

        return invalidPortIds.indexOf(port.id) === -1;
    }

    private _getInvalidBlockIds(flowId:number):string[]
    {
        const flowErrors = controllers.ui.flow.getFlowErrors(flowId);
        const blockIds = flowErrors.map(({flowBlockId}) => flowBlockId);

        return Array.from(new Set(blockIds));
    }

    private _getInvalidPortIds(flowId:number):string[]
    {
        const flowErrors = controllers.ui.flow.getFlowErrors(flowId);
        const portIds = flowErrors.map(({flowPortId}) => flowPortId);

        return Array.from(new Set(portIds));
    }

    private _getNestedErrors(prop:FlowProperty, block:FlowBlock):FlowValidationError[]
    {
        const {type} = prop.definition;
        const nestedProps = controllers.ui.flowPropertiesPanel.getNestedProperties(prop);
        const nestedErrors = this.validateFlowProperties(nestedProps, block);

        switch( type )
        {
            case PropertyType.Map:
            {
                this._validateMapWidgetKeyUniqueness(prop, block, nestedErrors);
                break;
            }
        }

        return nestedErrors;
    }

    private _validateMapWidgetKeyUniqueness(prop:FlowProperty,
                                            block:FlowBlock,
                                            // a mutable parameter
                                            nestedErrors:FlowValidationError[]):FlowValidationError[]
    {
        const {meta, definition} = prop;

        const fppController = controllers.ui.flowPropertiesPanel;
        const {key: keyDefinition} = definition.mapInfo;
        const items = meta && meta.items;

        if( items )
        {
            const itemsKeyMap = items.reduce((result, {key}) =>
            {
                result[key] = result[key] + 1 || 1;
                return result;
            }, {});

            items.forEach(({key}, index) =>
            {
                const isUnique = itemsKeyMap[key] === 1;
                const propName = fppController.getArrayPropertyName(keyDefinition.name, index);

                if( !isUnique )
                {
                    const error = nestedErrors.find(({flowPropertyName}) => flowPropertyName === propName);

                    if( error )
                    {
                        error.messages.push(ValidationMessages.uniqueKey);
                    }
                    else
                    {
                        nestedErrors.push(FlowValidationError.propertyError(
                            [ValidationMessages.uniqueKey],
                            block.id,
                            block.name,
                            propName
                        ));
                    }
                }
            });
        }

        return nestedErrors;
    }

    private _getTypeErrorMessages(prop:FlowProperty):string[]
    {
        const {value, definition} = prop;
        const {type} = definition;

        switch( type )
        {
            case PropertyType.Number:
            {
                if( !Validator.isNumber(value) )
                {
                    return [ValidationMessages.number];
                }

                break;
            }
        }

        return [];
    }

    private _getWidgetErrorMessages(prop:FlowProperty):string[]
    {
        const {value, definition} = prop;
        const widget = controllers.ui.flowPropertiesPanel.getWidgetName(definition);

        switch( widget )
        {
            case PropertyWidgetName.URL:
            {
                if( !Validator.isURL(value) )
                {
                    return [ValidationMessages.url];
                }

                break;
            }

            case PropertyWidgetName.Hostname:
            {
                if( !Validator.isHostname(value) )
                {
                    return [ValidationMessages.hostname];
                }

                break;
            }

            case PropertyWidgetName.Port:
            {
                if( !Validator.isPort(value) )
                {
                    return [ValidationMessages.port];
                }

                break;
            }

            case PropertyWidgetName.Email:
            {
                if( !Validator.isEmail(value) )
                {
                    return [ValidationMessages.email];
                }

                break;
            }
        }

        return [];
    }

    private _getCustomErrorMessages(prop:FlowProperty, props:FlowProperty[]):string[]
    {
        const {definition} = prop;
        const {validation} = definition;
        const messages = [];

        if( !validation )
        {
            return [];
        }

        const args = props.map(({definition}) => definition.name);
        const values = props.map(({value}) => value);

        const isPropValid = (rule):boolean =>
        {
            try
            {
                const fn = new Function(...args, `return ${rule};`);
                return fn(...values);
            }
            catch( err )
            {
                console.warn(`Warning: validation expr of ${definition.name} property is not valid`, err);
                return false;
            }
        };

        validation.forEach(({rule, message}) =>
        {
            const isValid = isPropValid(rule);

            if( !isValid )
            {
                messages.push(message);
            }
        });

        return messages;
    }
}

export class Validator
{
    public static isURL(value:string)
    {
        const reURL = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gi;
        return reURL.test(value);
    }

    public static isHostname(value:string)
    {
        const reHostname = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/gi;
        return reHostname.test(value);
    }

    public static isPort(value:string)
    {
        const rePort = /^\d+$/gi;
        return rePort.test(value);
    }

    public static isEmail(value:string)
    {
        const reEmail = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return reEmail.test(value);
    }

    public static isNumber(value:string)
    {
        const reNumber = /^-?\d+(\.\d+)?$/;
        return reNumber.test(value);
    }
}

export class ValidationMessages
{
    public static nonEmpty = 'Please specify a non-empty value.';
    public static uniqueKey = 'Key should be unique.';
    public static requiredPort = 'Input port requires connection.';
    public static number = 'Value should be a number.';
    public static url = 'Url is not valid.';
    public static hostname = 'Hostname is not valid.';
    public static port = 'Port should contain only digits.';
    public static email = 'Email is not valid.';
}
